import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthContext";

export interface Notification {
  id: string;
  user_id: string;
  type: string;
  icon: string;
  title: string;
  description: string | null;
  is_read: boolean;
  link: string | null;
  created_at: string;
  category?: string | null;
  severity?: string | null;
}

const PAGE_SIZE = 30;

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const requestRef = useRef(0);

  const refreshUnread = useCallback(async (userId: string) => {
    const { count, error: err } = await supabase
      .from("notifications")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("is_read", false);
    if (!err && typeof count === "number") setUnreadCount(count);
  }, []);

  const load = useCallback(async (userId: string) => {
    const ticket = ++requestRef.current;
    setLoading(true);
    const { data, error: err } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(PAGE_SIZE);

    if (ticket !== requestRef.current) return;
    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }
    const rows = (data as Notification[]) ?? [];
    setError(null);
    setNotifications(rows);
    setHasMore(rows.length === PAGE_SIZE);
    setLoading(false);
    void refreshUnread(userId);
  }, [refreshUnread]);

  const loadMore = useCallback(async () => {
    if (!user || loadingMore || !hasMore) return;
    const oldest = notifications[notifications.length - 1]?.created_at;
    if (!oldest) return;
    setLoadingMore(true);
    const { data, error: err } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", user.id)
      .lt("created_at", oldest)
      .order("created_at", { ascending: false })
      .limit(PAGE_SIZE);
    if (err) { setError(err.message); setLoadingMore(false); return; }
    const rows = (data as Notification[]) ?? [];
    setNotifications((prev) => {
      const seen = new Set(prev.map((n) => n.id));
      return [...prev, ...rows.filter((r) => !seen.has(r.id))];
    });
    setHasMore(rows.length === PAGE_SIZE);
    setLoadingMore(false);
  }, [user, notifications, hasMore, loadingMore]);

  useEffect(() => {
    if (!user) {
      setNotifications([]); setUnreadCount(0); setLoading(false); setError(null); setHasMore(false);
      return;
    }
    void load(user.id);

    // NotificationPanel can be mounted both in the global app chrome and in
    // Creative Studio's local header. A static topic such as
    // `notifications-realtime` is therefore unsafe: supabase.channel(topic)
    // reuses the same RealtimeChannel object, so the second hook instance can
    // attempt to register postgres_changes on a channel that is already joining
    // or subscribed. New supabase-js versions throw instead of allowing that.
    // Give each mounted hook instance its own topic and fully remove it on cleanup.
    const channelName = `notifications-realtime:${user.id}:${crypto.randomUUID()}`;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    try {
      channel = supabase
        .channel(channelName)
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
          (payload) => {
            if (payload.eventType === "INSERT") {
              const incoming = payload.new as Notification;
              setNotifications((prev) => prev.some((n) => n.id === incoming.id) ? prev : [incoming, ...prev]);
              if (!incoming.is_read) setUnreadCount((c) => c + 1);
            } else if (payload.eventType === "UPDATE") {
              const next = payload.new as Notification;
              setNotifications((prev) => prev.map((n) => (n.id === next.id ? next : n)));
              void refreshUnread(user.id);
            } else if (payload.eventType === "DELETE") {
              const goneId = (payload.old as { id?: string })?.id;
              setNotifications((prev) => prev.filter((n) => n.id !== goneId));
              void refreshUnread(user.id);
            }
          },
        )
        .subscribe((status, subscribeError) => {
          if (status === "SUBSCRIBED") void load(user.id);
          if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
            console.warn("[useNotifications] realtime unavailable; notifications remain query-backed.", subscribeError);
          }
        });
    } catch (realtimeError) {
      // Realtime is an enhancement. A subscription failure must never crash the
      // page that owns the notification bell.
      console.warn("[useNotifications] realtime subscription unavailable:", realtimeError);
      channel = null;
    }

    return () => {
      if (channel) {
        void supabase.removeChannel(channel);
        channel = null;
      }
    };
  }, [user, load, refreshUnread]);

  const markAsRead = async (id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
    const { error: err } = await supabase.from("notifications").update({ is_read: true }).eq("id", id);
    if (err) { setError(err.message); if (user) void load(user.id); }
  };

  const markAllAsRead = async () => {
    if (!user) return;
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
    const { error: err } = await supabase
      .from("notifications").update({ is_read: true }).eq("user_id", user.id).eq("is_read", false);
    if (err) { setError(err.message); void load(user.id); }
  };

  const deleteNotification = async (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    const { error: err } = await supabase.from("notifications").delete().eq("id", id);
    if (err) { setError(err.message); if (user) void load(user.id); }
    else if (user) void refreshUnread(user.id);
  };

  const retry = useCallback(() => { if (user) void load(user.id); }, [user, load]);

  return {
    notifications, unreadCount, loading, loadingMore, error, hasMore,
    markAsRead, markAllAsRead, deleteNotification, loadMore, retry,
  };
}
